import React, { useEffect, useMemo, useRef, useState } from 'react';
import _ from 'lodash';

import { DropdownBtn, DropdownDraw, DropdownOption, DropdownWrap } from './index.style';

import OPTIONS from './options';

import ICONS from '../../assets/icons';

interface DropdownProps {
  optionList: 'sort' | 'dateFilter';
  defaultVal: number;
  onChange: (value: string) => void;
}

// An option as the component uses it, whatever form it was given in: a named list, a list of objects, or a list of
// plain strings.
interface NormalisedOption {
  value: any;
  label: string;
}

const normalise = (optionList: any): NormalisedOption[] => {
  const options = typeof optionList === 'string' ? OPTIONS[optionList] : optionList;

  return options.map((option: any) => (_.isObject(option) ? { value: (option as any).value, label: (option as any).label } : { value: option, label: option }));
};

/**
 * A rounded select that matches the portal's pill toggle: a bordered button with a turning chevron, and a menu with a soft
 * shadow, a hover highlight and a tick on the chosen option. It closes on a click elsewhere or Escape, and is made of real
 * buttons, so it works from the keyboard. Behaviour is unchanged: it calls onChange with the starting value on mount and
 * again whenever the choice changes.
 */
const Dropdown: React.FC<DropdownProps> = ({ optionList, defaultVal, onChange }): React.ReactElement => {
  const options = useMemo(() => normalise(optionList), [optionList]);
  const [selectedVal, setSelectedVal] = useState(options[defaultVal].value);
  const [openDropdown, setOpenDropdown] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const selected = options.find((option) => option.value === selectedVal) ?? options[defaultVal];

  useEffect(() => {
    onChange(selectedVal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVal]);

  // Close on a click anywhere else, or on Escape, while it is open.
  useEffect(() => {
    if (!openDropdown) {
      return undefined;
    }

    const onPointer = (event: MouseEvent) => {
      if (wrapRef.current !== null && !wrapRef.current.contains(event.target as Node)) {
        setOpenDropdown(false);
      }
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpenDropdown(false);
      }
    };

    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);

    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [openDropdown]);

  return (
    <DropdownWrap ref={wrapRef}>
      <DropdownBtn
        type="button"
        open={openDropdown}
        aria-haspopup="listbox"
        aria-expanded={openDropdown}
        onClick={(e): void => {
          e.preventDefault();
          setOpenDropdown(!openDropdown);
        }}
      >
        <span>{selected.label}</span>
        <ICONS.chevronDown />
      </DropdownBtn>
      <DropdownDraw open={openDropdown} role="listbox">
        {options.map((option) => (
          <DropdownOption
            key={String(option.value)}
            type="button"
            role="option"
            aria-selected={option.value === selectedVal}
            active={option.value === selectedVal}
            onClick={(e): void => {
              e.preventDefault();
              setSelectedVal(option.value);
              setOpenDropdown(false);
            }}
          >
            {option.label}
          </DropdownOption>
        ))}
      </DropdownDraw>
    </DropdownWrap>
  );
};

export default Dropdown;
